// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useMemo } from 'react';
import Modal from './Modal';
import Button from './Button';
import { useCredits } from '../contexts/CreditsContext';
import ResourceManager from '../utils/game/ResourceManager';
import './TradeShop.css';

const ResourcesShop = ({ isOpen, onClose, location }) => {
  const { credits, removeCredits } = useCredits();
  const [buyQuantities, setBuyQuantities] = useState({});
  const [feedback, setFeedback] = useState(null);

  const resources = ResourceManager.getAllResources();
  const maxCapacity = ResourceManager.maxCapacity;
  const costs = ResourceManager.getResourceCosts();

  const resourceInfo = {
    food: { icon: '🌾', name: 'Food', unit: 'units' },
    water: { icon: '💧', name: 'Water', unit: 'units' },
    oxygen: { icon: '🫧', name: 'Oxygen', unit: 'units' },
    medical: { icon: '💊', name: 'Medical', unit: 'kits' },
    parts: { icon: '🔧', name: 'Parts', unit: 'units' }
  };

  const resourceList = useMemo(() => {
    return Object.keys(resourceInfo).map(id => ({
      id,
      ...resourceInfo[id],
      buyPrice: costs[id],
      owned: resources[id],
      max: maxCapacity[id]
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources, costs, maxCapacity]);

  const buyTotal = useMemo(() => {
    return Object.entries(buyQuantities).reduce((sum, [id, qty]) => {
      const resource = resourceList.find(r => r.id === id);
      return sum + (resource?.buyPrice || 0) * qty;
    }, 0);
  }, [buyQuantities, resourceList]);

  const handleBuy = () => {
    if (buyTotal > credits) {
      setFeedback({ type: 'error', message: 'Insufficient credits!' });
      setTimeout(() => setFeedback(null), 3000);
      return;
    }

    let purchased = [];
    Object.entries(buyQuantities).forEach(([id, qty]) => {
      if (qty > 0) {
        ResourceManager.addResource(id, qty);
        purchased.push(`${qty}x ${resourceInfo[id].name}`);
      }
    });

    if (purchased.length > 0) {
      removeCredits(buyTotal);
      setFeedback({ type: 'success', message: `✅ Purchased: ${purchased.join(', ')}` });
      setBuyQuantities({});
      setTimeout(() => setFeedback(null), 3000);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="🌾 SHIP RESOURCES" size="large">
      <div className="trade-shop-content">
        <div className="trade-header">
          <h3>{location?.name} - Resource Depot</h3>
          <div className="credits-display">Credits: {credits} ¢</div>
        </div>

        {feedback && (
          <div className={`trade-feedback ${feedback.type}`}>
            {feedback.message}
          </div>
        )}

        <div className="trade-section">
          <div className="trade-goods-list">
            {resourceList.map(resource => {
              const qty = buyQuantities[resource.id] || 0;
              const canBuy = resource.owned < resource.max;
              const maxBuy = Math.floor(Math.min(
                resource.max - resource.owned,
                Math.floor(credits / resource.buyPrice)
              ));

              return (
                <div key={resource.id} className="trade-good-item">
                  <div className="good-info">
                    <span className="good-icon">{resource.icon}</span>
                    <div className="good-details">
                      <div className="good-name">{resource.name}</div>
                      <div className="good-stats">
                        Owned: {Math.floor(resource.owned)}/{Math.floor(resource.max)} {resource.unit}
                      </div>
                    </div>
                  </div>
                  <div className="good-price">
                    {resource.buyPrice} ¢
                  </div>
                  <div className="good-controls">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => setBuyQuantities(prev => ({
                        ...prev,
                        [resource.id]: Math.max(0, (prev[resource.id] || 0) - 1)
                      }))}
                      disabled={qty === 0}
                    >
                      -
                    </Button>
                    <span className="quantity">{Math.floor(qty)}</span>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => setBuyQuantities(prev => ({
                        ...prev,
                        [resource.id]: Math.min(maxBuy, (prev[resource.id] || 0) + 1)
                      }))}
                      disabled={!canBuy || qty >= maxBuy}
                    >
                      +
                    </Button>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => setBuyQuantities(prev => ({
                        ...prev,
                        [resource.id]: maxBuy
                      }))}
                      disabled={!canBuy || maxBuy === 0}
                    >
                      MAX
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="trade-summary">
            <div className="summary-line">
              <span>Total Cost:</span>
              <span className="summary-value">{buyTotal} ¢</span>
            </div>
            <Button
              variant="primary"
              size="large"
              onClick={handleBuy}
              disabled={buyTotal === 0 || buyTotal > credits}
            >
              PURCHASE RESOURCES
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ResourcesShop;
